import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Sparkles, Star } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadRazorpay } from "@/lib/razorpay";
import { inr } from "@/lib/brand";

const PLAN_ICONS = { silver: Star, gold: Sparkles, platinum: Crown };
const PLAN_STYLES = {
  silver: {
    ring: "border-stone-200",
    bg: "bg-white",
    button: "bg-stone-900 text-white hover:bg-stone-700",
    badge: "bg-stone-100 text-stone-700",
  },
  gold: {
    ring: "border-amber-300 ring-2 ring-amber-200",
    bg: "bg-gradient-to-b from-amber-50 to-white",
    button: "bg-amber-500 text-white hover:bg-amber-600",
    badge: "bg-amber-500 text-white",
  },
  platinum: {
    ring: "border-stone-900",
    bg: "bg-gradient-to-b from-stone-900 to-stone-800 text-white",
    button: "bg-white text-stone-900 hover:bg-stone-100",
    badge: "bg-rose-500 text-white",
  },
};

export default function Membership({ onAuth }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/memberships/plans").then((r) => {
      setPlans((r.data.plans || []).filter((p) => p.key !== "apartment_access"));
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (!user) { setActive(null); return; }
    api.get("/memberships/my")
      .then((r) => {
        if (!r.data.active) { setActive(null); return; }
        const m = (r.data.memberships || []).find(x => x.plan_key !== "apartment_access");
        setActive(m || null);
      })
      .catch(() => setActive(null));
  }, [user]);

  const startCheckout = async (planKey) => {
    setErr("");
    if (!user) {
      if (typeof onAuth === "function") onAuth();
      return;
    }
    setLoadingKey(planKey);
    try {
      const Razorpay = await loadRazorpay();
      const { data } = await api.post("/memberships/create-order", { plan: planKey });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "One Light Stays",
        description: `${data.plan.name} Membership`,
        order_id: data.order_id,
        prefill: {
          name: data.user.name || "",
          email: data.user.email || "",
          contact: data.user.phone || "",
        },
        theme: { color: "#1c1917" },
        handler: async (resp) => {
          try {
            await api.post("/memberships/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (typeof refresh === "function") await refresh();
            nav("/membership/success");
          } catch (e) {
            setErr(formatError(e));
          }
        },
        modal: {
          ondismiss: () => setLoadingKey(null),
        },
      };
      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setErr(resp?.error?.description || "Payment failed. Please try again.");
        setLoadingKey(null);
      });
      rzp.open();
    } catch (e) {
      setErr(formatError(e));
      setLoadingKey(null);
    }
  };

  return (
    <section id="membership" className="max-w-7xl mx-auto px-6 lg:px-10 my-20" data-testid="membership-section">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-rose-500 mb-2">
          <Crown className="w-4 h-4" /> One Light Members Club
        </div>
        <h2 className="font-display text-3xl md:text-4xl mb-3">Unlock Exclusive Stays with Membership</h2>
        <p className="text-stone-500 text-base max-w-2xl mx-auto">
          Members get instant benefits and offers on our curated stays.
        </p>
      </div>

      {active && (
        <div className="max-w-3xl mx-auto mb-8 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="membership-active-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-emerald-900">
                You are a {active.plan_name} member 🎉
              </div>
              <div className="text-xs text-emerald-700">
                Valid till {new Date(active.valid_till).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            </div>
          </div>
          <button
            onClick={() => nav("/profile?tab=membership")}
            className="btn-primary text-sm !bg-emerald-600 hover:!bg-emerald-700"
            data-testid="active-view-membership-details"
          >
            View Membership Details
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.key] || Star;
          const style = PLAN_STYLES[p.key] || PLAN_STYLES.silver;
          const isActive = active?.plan_key === p.key;
          return (
            <div
              key={p.key}
              className={`relative rounded-2xl border ${style.ring} ${style.bg} p-6 flex flex-col`}
              data-testid={`membership-plan-${p.key}`}
            >
              {p.tag && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[0.65rem] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${style.badge}`}>
                  {p.tag}
                </div>
              )}
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${p.key === "platinum" ? "bg-white/10" : "bg-stone-100"}`}>
                <Icon className={`w-5 h-5 ${p.key === "platinum" ? "text-amber-300" : "text-stone-700"}`} strokeWidth={1.5} />
              </div>
              <div className="font-display text-2xl mb-1">{p.name}</div>
              <div className={`text-xs uppercase tracking-wider mb-4 ${p.key === "platinum" ? "text-stone-300" : "text-stone-500"}`}>
                {p.duration_months === 13 ? "12" : p.duration_months} months membership {p.duration_months === 13 ? "+ 1 month free trial" : ""}
              </div>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="font-display text-4xl">{inr(p.amount)}</span>
                <span className={`text-xs ${p.key === "platinum" ? "text-stone-300" : "text-stone-500"}`}>one-time</span>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {(p.perks || []).map((perk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 ${p.key === "platinum" ? "text-amber-300" : "text-emerald-600"}`} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout(p.key)}
                disabled={loadingKey === p.key || isActive}
                className={`w-full rounded-full py-2.5 text-sm font-semibold transition ${style.button} disabled:opacity-60`}
                data-testid={`buy-plan-${p.key}`}
              >
                {isActive ? "Your Current Plan" : loadingKey === p.key ? "Opening…" : `Buy ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {err && (
        <div className="mt-4 text-center text-sm text-rose-600" data-testid="membership-error">{err}</div>
      )}

      <div className="text-center mt-6 text-xs text-stone-500">
        Secured by Razorpay
      </div>
    </section>
  );
}
